import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { KeyRound, UserPlus } from 'lucide-react'
import { ROLES, type Role } from '@emag/shared'
import { api, apiErrorMessage } from '../lib/api'
import { useAuthStore } from '../stores/auth'

interface UserRow {
  id: number
  email: string
  name: string
  role: Role
  active: boolean
  mustChangePassword: boolean
  lockedUntil: string | null
  createdAt: string
}

const ROLE_LABEL: Record<Role, string> = {
  admin: 'Administrator',
  operator: 'Operator',
}

const btn =
  'rounded-md bg-b4l-green px-3 py-1.5 text-[13px] text-white hover:opacity-90 disabled:opacity-50'
const btnGhost =
  'rounded-md border border-b4l-border px-2.5 py-1 text-[12px] text-b4l-text-muted hover:bg-b4l-bg-soft disabled:opacity-40'

/** Parolă inițială ușor de citit la telefon, dar suficient de lungă. */
function suggestPassword(): string {
  const abc = 'abcdefghijkmnpqrstuvwxyz'
  const ABC = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const cif = '23456789'
  const pick = (s: string, n: number) =>
    Array.from({ length: n }, () => s[Math.floor(Math.random() * s.length)]).join('')
  return `${pick(ABC, 1)}${pick(abc, 5)}-${pick(cif, 4)}-${pick(abc, 4)}`
}

export function SetariPage() {
  const qc = useQueryClient()
  const meId = useAuthStore((s) => s.user?.id)

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.get<{ users: UserRow[] }>('/users')).data.users,
  })

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<Role>('operator')
  const [password, setPassword] = useState(suggestPassword())
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null)

  const create = useMutation({
    mutationFn: async () => (await api.post('/users', { email, name, role, password })).data,
    onSuccess: () => {
      // Parola se arata O SINGURA DATA, aici: nu se mai poate citi de nicaieri dupa.
      setCreated({ email: email.trim().toLowerCase(), password })
      setEmail('')
      setName('')
      setPassword(suggestPassword())
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  })

  async function patch(id: number, data: Partial<Pick<UserRow, 'role' | 'active'>>) {
    try {
      await api.patch(`/users/${id}`, data)
      void qc.invalidateQueries({ queryKey: ['users'] })
      toast.success('Salvat')
    } catch (err) {
      toast.error(apiErrorMessage(err), { duration: 8000 })
    }
  }

  async function resetPass(u: UserRow) {
    const pass = suggestPassword()
    if (!window.confirm(`Resetezi parola pentru ${u.email}?\n\nNoua parolă: ${pass}\n\nO notezi acum — nu se mai poate citi după.`))
      return
    try {
      await api.post(`/users/${u.id}/reset-password`, { password: pass })
      setCreated({ email: u.email, password: pass })
      void qc.invalidateQueries({ queryKey: ['users'] })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate()
  }

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="mb-5 text-lg font-semibold text-b4l-text">Setări</h1>

      <div className="mb-4 rounded-xl border border-b4l-border bg-white p-5">
        <h2 className="mb-1 text-[14px] font-semibold text-b4l-text">Utilizatori</h2>
        <p className="mb-3 text-[11px] leading-snug text-b4l-text-dim">
          <b>Administratorul</b> poate tot: configurări de client, credențiale, pornirea scraperului, ștergerea
          importurilor. <b>Operatorul</b> vede datele și generează fișiere, dar nu umblă la setări.
        </p>

        <div className="space-y-1.5">
          {(users ?? []).map((u) => (
            <div
              key={u.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-b4l-border px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-[13px] text-b4l-text">
                  {u.name}
                  {u.id === meId && <span className="ml-2 text-[11px] text-b4l-text-dim">(tu)</span>}
                  {!u.active && <span className="ml-2 text-[11px] text-red-600">dezactivat</span>}
                  {u.mustChangePassword && (
                    <span className="ml-2 text-[11px] text-b4l-text-dim">trebuie să-și schimbe parola</span>
                  )}
                </div>
                <div className="text-[11px] text-b4l-text-dim">{u.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={u.role}
                  onChange={(e) => void patch(u.id, { role: e.target.value as Role })}
                  className="rounded-md border border-b4l-border px-2 py-1 text-[12px] outline-none focus:border-b4l-green"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </option>
                  ))}
                </select>
                <button onClick={() => void resetPass(u)} className={btnGhost}>
                  <KeyRound size={12} className="mr-1 inline" />
                  Resetează parola
                </button>
                <button onClick={() => void patch(u.id, { active: !u.active })} className={btnGhost}>
                  {u.active ? 'Dezactivează' : 'Reactivează'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {created && (
        <div className="mb-4 rounded-xl border border-b4l-green bg-b4l-bg-soft p-5">
          <h2 className="mb-1 text-[14px] font-semibold text-b4l-text">Notează parola acum</h2>
          <p className="mb-2 text-[11px] text-b4l-text-dim">
            Nu se mai poate citi de nicăieri după ce închizi mesajul. Utilizatorul va fi obligat s-o schimbe la
            prima autentificare.
          </p>
          <pre className="mb-2 overflow-x-auto rounded-md bg-white px-3 py-2 text-[13px] text-b4l-text">
{created.email}
{created.password}
          </pre>
          <button onClick={() => setCreated(null)} className={btnGhost}>
            Am notat, închide
          </button>
        </div>
      )}

      <div className="rounded-xl border border-b4l-border bg-white p-5">
        <h2 className="mb-3 text-[14px] font-semibold text-b4l-text">Adaugă utilizator</h2>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-[12px] text-b4l-text-muted">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] text-b4l-text-muted">Nume</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green"
              />
            </div>
            <div>
              <label className="mb-1 block text-[12px] text-b4l-text-muted">Rol</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className="w-full rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] text-b4l-text-muted">Parolă inițială</label>
              <div className="flex gap-2">
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={10}
                  required
                  className="w-full rounded-md border border-b4l-border px-3 py-1.5 text-[13px] outline-none focus:border-b4l-green"
                />
                <button type="button" onClick={() => setPassword(suggestPassword())} className={btnGhost}>
                  Alta
                </button>
              </div>
            </div>
          </div>
          <button type="submit" disabled={create.isPending} className={btn}>
            <UserPlus size={14} className="mr-1 inline" />
            {create.isPending ? 'Se creează…' : 'Creează utilizator'}
          </button>
        </form>
      </div>
    </div>
  )
}
